import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Barcode from 'pages/Settings/Print/components/common/Barcode';
import PrintTable from 'pages/Settings/Print/components/common/PrintTable';
import usePrintHelper from 'pages/Settings/Print/hooks/useHelpers';

import { pdfScales } from './MainTemplate';
import RTLTextForPDF from './RTLTextForPDF';
import { itemConfig } from './Template1';
import st from './defaultStyles';

/**
 *
 * @author @omerrkosar
 */

// Template 1: Order Print - QR Code (A4)

const Template3 = (props) => {
	const {
		orderID,
		orderDate,
		grandTotal,
		subTotal,
		totalDue,
		discountAmount,
		codFee,
		taxAmount,
		shippingAmount,
		shippingDiscountAmount,
		VATNumber,
		CRNumber,
		paymentMethod,
		receiverName,
		receiverAddress,
		receiverCity,
		receiverCountry,
		currency,
		receiverTelephone,
		senderName,
		senderAddress,
		invoiceAddress,
		items = [],
		shipmentNumber,
		shippingCompanyName,
		dateOfShipmentArrival,
		shippingDescription,
		orderComment = null,
		companyLogo = null,
		brandLogo = null,
		qrCode = null,
		couponCode = null,
		propJSON,
		ref2
	} = props;

	const {
		showOrderItems = true,
		showQrCode = true,
		showVatNumber = true,
		showCRNumber = true,
		showOrderNumber = true,
		showBarcode = true,
		showOrderDate = true,
		showInvoiceCreatedDate = true,
		showGrandTotal = true,
		showTotalDue = true,
		showPaymentMethod = true,
		showPhoneNumber = true,
		showFullName = true,
		showSenderName = true,
		showSenderAddress = true,
		showAddress = true,
		showCity = false,
		showCountry = false,
		showVatAmount = true,
		showShipFee = true,
		showShippingDiscount = false,
		showDiscount = true,
		showCodFee = false,
		showSubTotal = true,
		showTotalQuantity = true,
		showShipmentNumber = true,
		showDeliveryCompany = true,
		showDateOfShipmentArrival = true,
		showShippingDescription = true,
		showOrderComment = false,
		showCouponCode = false,
		logoType = 'companyLogo',
		pageSize = 'A4',
		fontSize = '10px',
		barcodeSize = 'default',
		orderNumberBarcodeFormat = 'CODE128',
		language,
		title,
		columnSettings = [],
		showRef2 = false
	} = propJSON;

	const { t } = useTranslation('translation', {
		lng: language
	});

	const { getTemplateFontSize, getBarcodeSize } = usePrintHelper();

	const pageScale = pdfScales[pageSize];
	const calculatedFontSize = getTemplateFontSize(fontSize, pageScale);
	const barcodeWidth = getBarcodeSize(barcodeSize);

	const totalQuantity = useMemo(() => items?.reduce((acc, item) => acc + item.quantity, 0), [items]);

	const styles = useMemo(
		() =>
			StyleSheet.create({
				logo: {
					width: 70 * pageScale
				},
				section: {
					paddingTop: 10,
					paddingBottom: 10,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center'
				},
				table: {
					display: 'flex',
					width: '100%',
					borderStyle: 'solid',
					borderWidth: 1,
					borderRightWidth: 1,
					borderLeftWidth: 1,
					borderBottomWidth: 1,
					borderTopWidth: 1,
					borderRadius: 5,
					borderColor: '#ccc',
					marginBottom: 10
				},
				tableRow: {
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					verticalAlign: 'top',
					borderBottomWidth: 0.5,
					borderTopWidth: 0,
					borderLeftWidth: 0,
					borderRightWidth: 0
				},
				tableCol: {
					borderStyle: 'solid',
					borderColor: '#ccc',
					marginHorizontal: 2
				},
				tableCell: {
					marginTop: 0,
					paddingHorizontal: 2,
					textAlign: 'left'
				},
				detailsRow: {
					width: '100%',
					display: 'flex',
					justifyContent: 'flex-end',
					flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
					backgroundColor: '#f5f5f5',
					borderBottomWidth: 0.5
				},
				detailsLeftCell: {
					flex: '0 0 25%',
					paddingHorizontal: 2
				},
				detailsSemi: { flex: '0 0 2%', textAlign: 'center' },
				detailsRightCell: {
					flex: '0 0 15%',
					textAlign: 'left',
					paddingHorizontal: 2
				},
				header: { fontSize: 1.5 * calculatedFontSize },
				subTitle: {
					fontSize: 1.3 * calculatedFontSize,
					textAlign: language === 'arabicValue' ? 'right' : 'left'
				},
				qrCodePic: {
					width: 70 * pageScale,
					height: 70 * pageScale,
					objectFit: 'cover'
				},
				barcode: {
					width: `${barcodeWidth}%`
				}
			}),
		[language, pageScale, calculatedFontSize, barcodeWidth]
	);

	return (
		<View>
			{/** header */}
			<View
				style={[
					styles.tableRow,
					{ justifyContent: 'space-between', border: 'none', alignItems: 'center' },
					(!brandLogo || !companyLogo) && !showQrCode ? { justifyContent: 'center' } : {}
				]}>
				<View style={logoType === 'noLogo' ? styles.logo : {}}>
					{logoType === 'companyLogo' && companyLogo && <Image src={companyLogo} style={styles.logo} />}
					{logoType === 'brandLogo' && brandLogo && <Image src={brandLogo} style={styles.logo} />}
				</View>
				{/** barcode section*/}
				{showBarcode && (
					<Barcode
						value={orderID}
						options={{
							format: orderNumberBarcodeFormat,
							displayValue: false,
							flat: true,
							margin: 0
						}}
						barcodeStyles={[styles.barcode]}
						textStyles={{ fontSize: calculatedFontSize + 5 }}
					/>
				)}
				<View style={!showQrCode ? styles.qrCodePic : {}}>
					{showQrCode && qrCode && <Image key="qrCode" style={[styles.qrCodePic]} src={qrCode} />}
				</View>
			</View>

			<View style={[styles.section, styles.header]}>
				<Text style={[st.textArabic]}>{title ? title : t('simplifiedTaxInvoice')}</Text>
			</View>
			{/** section-1 */}
			<View style={[styles.tableRow, { marginBottom: 15 }]}>
				<View style={{ width: '100%' }}>
					<View>
						{showOrderNumber && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('orderNo')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{orderID}</Text>
							</View>
						)}
						{showOrderDate && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('orderDate')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{orderDate}</Text>
							</View>
						)}
						{showInvoiceCreatedDate && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('invoiceCreatedDate')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{orderDate}</Text>
							</View>
						)}
						{showDateOfShipmentArrival && dateOfShipmentArrival && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('dateOfShipmentArrival')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{dateOfShipmentArrival}</Text>
							</View>
						)}
						{showDeliveryCompany && shippingCompanyName && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('deliveryCompany')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{shippingCompanyName}</Text>
							</View>
						)}
						{showShipmentNumber && shipmentNumber && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('shipmentNumber')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{shipmentNumber}</Text>
							</View>
						)}
						{showPaymentMethod && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('payment')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{t(paymentMethod)}</Text>
							</View>
						)}
						{showVatNumber && VATNumber && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('vatNumber')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{VATNumber}</Text>
							</View>
						)}
						{showCRNumber && CRNumber && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('crNumber')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{CRNumber}</Text>
							</View>
						)}
						{showFullName && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('fullName')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{receiverName}</Text>
							</View>
						)}
						{showPhoneNumber && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('telephone')} </Text>
								<Text style={styles.tableCell}> : </Text>
								<Text style={styles.tableCell}>{receiverTelephone}</Text>
							</View>
						)}
						{showAddress && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('address')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text
									style={[
										styles.tableCell,
										{ width: '100%', textAlign: language === 'arabicValue' ? 'right' : 'left' }
									]}>
									{receiverAddress && <RTLTextForPDF text={receiverAddress} />}
								</Text>
							</View>
						)}
						{showCity && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('city')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text
									style={[
										styles.tableCell,
										{ width: '100%', textAlign: language === 'arabicValue' ? 'right' : 'left' }
									]}>
									{receiverCity}
								</Text>
							</View>
						)}
						{showCountry && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('country')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text
									style={[
										styles.tableCell,
										{ width: '100%', textAlign: language === 'arabicValue' ? 'right' : 'left' }
									]}>
									{t(`country.${receiverCountry}`)}
								</Text>
							</View>
						)}
						{showSenderName && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('senderName')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{senderName}</Text>
							</View>
						)}
						{showSenderAddress && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('senderAddress')} </Text>
								<Text style={styles.tableCell}>:</Text>
								{(invoiceAddress || senderAddress) && (
									<RTLTextForPDF text={invoiceAddress ? invoiceAddress : senderAddress} />
								)}
							</View>
						)}
						{showShippingDescription && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('shippingDescription')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{shippingDescription ?? '-'}</Text>
							</View>
						)}
						{showOrderComment && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('orderComments')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{orderComment ?? '-'}</Text>
							</View>
						)}
						{showCouponCode && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('couponCode')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{couponCode ?? '-'}</Text>
							</View>
						)}
						{showRef2 && (
							<View style={[styles.tableRow, { border: 'none' }]}>
								<Text style={styles.tableCell}>{t('ref2')} </Text>
								<Text style={styles.tableCell}>:</Text>
								<Text style={styles.tableCell}>{ref2}</Text>
							</View>
						)}
					</View>
				</View>
			</View>

			{/** section-2 */}
			<View style={[{ marginTop: 5 }]}>
				{showOrderItems && (
					<View>
						<Text style={[styles.subTitle]}>{t('items')}</Text>
					</View>
				)}
				<View style={styles.table}>
					{showOrderItems && (
						<PrintTable
							styles={{
								thead: {
									theadRow: [styles.tableRow, { backgroundColor: '#f5f5f5' }],
									theadCol: styles.tableCol,
									theadCell: {
										textAlign: language === 'arabicValue' ? 'right' : 'left',
										flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row'
									}
								},
								tbody: {
									tbodyRow: styles.tableRow,
									tbodyCol: styles.tableCol,
									tbodyCell: {
										textAlign: language === 'arabicValue' ? 'right' : 'left',
										flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row'
									}
								},
								barcode: {
									width: '100%'
								}
							}}
							itemConfig={itemConfig}
							items={items}
							columnSettings={columnSettings}
							wrap={true}
							language={language}
						/>
					)}
					{/** extra informations */}
					<View>
						{showTotalQuantity && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('totalQuantity')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>{totalQuantity}</Text>
							</View>
						)}
						{showSubTotal && subTotal && (
							<View>
								<View style={[styles.detailsRow]}>
									<Text style={[styles.detailsLeftCell]}>{t('subtotal')}</Text>
									<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
									<Text style={[styles.detailsRightCell]}>
										{subTotal}&nbsp;{currency}
									</Text>
								</View>
							</View>
						)}
						{showDiscount && discountAmount && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('discount_amount')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{discountAmount}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showCodFee && codFee && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('codFee')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{codFee}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showShipFee && shippingAmount && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('shipping')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{shippingAmount}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showShippingDiscount && shippingDiscountAmount && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('shippingDiscount')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{shippingDiscountAmount}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showVatAmount && taxAmount && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('vatAmount')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{taxAmount}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showTotalDue && (
							<View style={[styles.detailsRow]}>
								<Text style={[styles.detailsLeftCell]}>{t('totalDue')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{totalDue}&nbsp;{currency}
								</Text>
							</View>
						)}
						{showGrandTotal && (
							<View style={[styles.detailsRow, { borderBottomWidth: 0 }]}>
								<Text style={[styles.detailsLeftCell]}>{t('grandTotal')}</Text>
								<Text style={[styles.detailsSemi]}>&nbsp;:&nbsp;</Text>
								<Text style={[styles.detailsRightCell]}>
									{grandTotal}&nbsp;{currency}
								</Text>
							</View>
						)}
					</View>
				</View>
			</View>
		</View>
	);
};

export default Template3;
